import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

interface ApiResponse<T = any> {
  id?: string;
  timestamp?: string;
  data: T;
  error?: string | null;
}

interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

interface TokenConfig {
  key: string;
  storage?: 'localStorage' | 'sessionStorage';
  prefix?: string;
}

interface ApiServiceConfig {
  baseURL?: string;
  timeout?: number;
  tokenConfig?: TokenConfig;
  headers?: Record<string, string>;
  strictResponseValidation?: boolean;
}

class ApiService {
  private axiosInstance: AxiosInstance;
  private logoutCallback?: () => void;
  private tokenConfig: TokenConfig;
  private strictResponseValidation: boolean;

  constructor(config: ApiServiceConfig | string = {}) {
    const serviceConfig: ApiServiceConfig = typeof config === 'string' ? { baseURL: config } : config;

    const {
      baseURL = import.meta.env.VITE_BASE_URL,
      timeout = 30000,
      tokenConfig = {
        key: 'accessToken',
        storage: 'localStorage',
        prefix: 'Bearer'
      },
      headers = { 'Content-Type': 'application/json' },
      strictResponseValidation = true
    } = serviceConfig;

    console.log("Invoking backend api from host " + window.location.hostname);

    this.tokenConfig = tokenConfig; 
    this.strictResponseValidation = strictResponseValidation;

    this.axiosInstance = axios.create({
      baseURL,
      timeout,
      headers
    });

    this.setupInterceptors();
  }

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private getStorage() {
    return this.tokenConfig.storage === 'sessionStorage' ? sessionStorage : localStorage;
  }

  private getToken(): string | null {
    const storage = this.getStorage();
    return storage.getItem(this.tokenConfig.key);
  }

  private setToken(token: string): void {
    const storage = this.getStorage();
    storage.setItem(this.tokenConfig.key, token);
  }

  private removeToken(): void {
    const storage = this.getStorage();
    storage.removeItem(this.tokenConfig.key);
  }

  private isTokenExpired(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      if (!payload.exp) {
        return false;
      }

      return payload.exp < currentTime;
    } catch {
      return true;
    }
  }

  private formatError(error: any): ApiError {
    if (error.response) {
      return {
        message: error.response.data?.message || error.message || 'Request failed',
        status: error.response.status,
        code: error.response.data?.code || `HTTP_${error.response.status}`
      };
    }

    if (error.request) {
      return {
        message: 'Network error - please check your connection',
        code: 'NETWORK_ERROR'
      };
    }

    return {
      message: error.message || 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR'
    };
  }

  private handleApiResponse<T>(response: AxiosResponse): T {
    const responseData = response.data;

    if (!this.strictResponseValidation) {
      return responseData;
    }

    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
      if (responseData.error) {
        throw new Error(responseData.error);
      }
      return responseData.data;
    }

    return responseData;
  }

  private setupInterceptors() {
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig<any>) => {
        const token = this.getToken();

        if (token) {
          if (this.isTokenExpired(token)) {
            this.handleTokenExpiration();
            return Promise.reject(new Error('Token expired'));
          }

          const prefix = this.tokenConfig.prefix ? `${this.tokenConfig.prefix} ` : '';
          config.headers.Authorization = `${prefix}${token}`;
        }

        return config;
      },
      error => Promise.reject(this.formatError(error))
    );

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        if (this.strictResponseValidation && response.data?.error) {
          const error = new Error(response.data.error);
          return Promise.reject(this.formatError(error));
        }
        return response;
      },
      error => {
        if (error.response?.status === 401) {
          this.handleTokenExpiration();
        }
        return Promise.reject(this.formatError(error));
      }
    );
  }

  private handleTokenExpiration() {
    this.removeToken();
    this.logoutCallback?.();
  }

  async get<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse = await this.axiosInstance.get(url, { params, ...config });
    return this.handleApiResponse<T>(response);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse = await this.axiosInstance.post(url, data, config);
    return this.handleApiResponse<T>(response);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse = await this.axiosInstance.put(url, data, config);
    return this.handleApiResponse<T>(response);
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse = await this.axiosInstance.patch(url, data, config);
    return this.handleApiResponse<T>(response);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse = await this.axiosInstance.delete(url, config);
    return this.handleApiResponse<T>(response);
  }

  fetchExternalData = async (url: string, disableCache: boolean = false): Promise<any> => {
    try {
      const response = await fetch(url, {
        cache: disableCache ? 'no-store' : 'default'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return null;
    }
  };

  postExternalData = async (url: string, payload: any = {}): Promise<any> => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          error: true,
          status: response.status,
          message: data?.message || 'Unknown error occurred',
          data
        };
      }

      return data;
    } catch (error: any) {
      return {
        error: true,
        message: error?.message || 'Unexpected error'
      };
    }
  };

  isAuthenticated(): boolean {
    const token = this.getToken();
    return token ? !this.isTokenExpired(token) : false;
  }

  login(token: string): void {
    this.setToken(token);
  }

  logout(): void {
    this.removeToken();
  }

  getAuthToken(): string | null {
    return this.getToken();
  }
}

export const apiService = new ApiService({
  baseURL: import.meta.env.VITE_BASE_URL,
  tokenConfig: {
    key: 'accessToken',
    storage: 'localStorage',
    prefix: 'Bearer'
  }
});

export default apiService;
